import { IAddSocialMediaBulkResponseRow } from "@shared/lib/types/influencer";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { Tag } from "@shared/ui";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import React from "react";
import processStyles from "@features/AddCreator/ui/BulkAddModal/process.module.scss";

export interface CreatorRowsPreviewProps {
  validRows: IAddSocialMediaBulkResponseRow[];
  failedRows: IAddSocialMediaBulkResponseRow[];
  activeTab: "ready" | "failed";
  onTabChange: (tab: "ready" | "failed") => void;
}

export const CreatorRowsPreview: React.FC<CreatorRowsPreviewProps> = ({
  validRows,
  failedRows,
  activeTab,
  onTabChange,
}) => {
  return (
    <div style={{ marginTop: 16 }}>
      <div className={processStyles.tabCards}>
        <button
          type="button"
          onClick={() => onTabChange("ready")}
          className={`${processStyles.tabCard} ${
            activeTab === "ready" ? processStyles.tabCardProcessedActive : ""
          }`}
        >
          <div
            className={`${processStyles.tabCardIcon} ${processStyles.tabCardIconSuccess}`}
          >
            <CheckCircle2 size={18} />
          </div>
          <div>
            <div className={processStyles.tabCardCountSuccess}>
              {validRows.length}
            </div>
            <div className={processStyles.tabCardLabelSuccess}>Ready to add</div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => onTabChange("failed")}
          className={`${processStyles.tabCard} ${
            activeTab === "failed" ? processStyles.tabCardErrorActive : ""
          }`}
        >
          <div
            className={`${processStyles.tabCardIcon} ${processStyles.tabCardIconError}`}
          >
            <AlertCircle size={18} />
          </div>
          <div>
            <div className={processStyles.tabCardCountError}>
              {failedRows.length}
            </div>
            <div className={processStyles.tabCardLabelError}>Couldn’t import</div>
          </div>
        </button>
      </div>

      <div style={{ marginTop: 14 }}>
        {activeTab === "ready" ? (
          validRows.length > 0 ? (
            <div
              style={{
                border: "1px solid #e3e8ee",
                borderRadius: 8,
                maxHeight: 180,
                overflowY: "auto",
                background: "#ffffff",
              }}
            >
              {validRows.map((c, i) => {
                const isYt = c.platform?.toLowerCase() === "youtube";
                return (
                  <div
                    key={`${c.platform}-${c.username}-${c.rowNumber}-${i}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 14px",
                      borderBottom:
                        i < validRows.length - 1 ? "1px solid #f1f5f9" : "none",
                      fontSize: 13,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {getPlatformIcon(isYt ? "youtube" : "instagram")}
                      <strong>@{c.username}</strong>
                    </span>
                    <Tag color={isYt ? "error" : "purple"}>
                      {isYt ? "YouTube" : "Instagram"}
                    </Tag>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: "#697386",
                fontSize: 13,
                border: "1px dashed #e3e8ee",
                borderRadius: 8,
              }}
            >
              No valid creators found to add.
            </div>
          )
        ) : failedRows.length > 0 ? (
          <div
            style={{
              border: "1px solid #fed7aa",
              borderRadius: 8,
              maxHeight: 180,
              overflowY: "auto",
              background: "#fffaf5",
            }}
          >
            {failedRows.map((c, i) => {
              const isYt = c.platform?.toLowerCase() === "youtube";
              return (
                <div
                  key={`failed-${c.platform}-${c.username}-${c.rowNumber}-${i}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 14px",
                    borderBottom:
                      i < failedRows.length - 1 ? "1px solid #fed7aa" : "none",
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ color: "#9ca3af", fontSize: 11 }}>
                      Row {c.rowNumber}:
                    </span>
                    {getPlatformIcon(isYt ? "youtube" : "instagram")}
                    <strong>@{c.username || "unknown"}</strong>
                  </span>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        color: "#dc2626",
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={c.message}
                    >
                      {c.message || "Failed"}
                    </span>
                    <Tag color={isYt ? "error" : "purple"}>
                      {isYt ? "YouTube" : "Instagram"}
                    </Tag>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              color: "#16a34a",
              fontSize: 13,
              border: "1px dashed #86efac",
              borderRadius: 8,
              background: "#f0fdf4",
            }}
          >
            All creators passed validation!
          </div>
        )}
      </div>
    </div>
  );
};
